import { AddAPhoto, HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import {
  VehicleOdometerAfterModificationPhotoDto,
  vehicleOdometerAfterModificationPhotoValidationSchema
} from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'

interface OdometerAfterModificationPhotoDialogProps {
  initialValues: VehicleOdometerAfterModificationPhotoDto
  isLoading: boolean
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleOdometerAfterModificationPhotoDto) => void
  open: boolean
  vehicleGuid: string
}

export default function OdometerAfterModificationPhotoDialog({
  initialValues,
  isLoading,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: OdometerAfterModificationPhotoDialogProps) {
  const [src, setSrc] = useState<string>('')

  useEffect(() => {
    if (initialValues.documentGuid && vehicleGuid) {
      controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, initialValues.documentGuid).then(base64 => {
        setSrc(`data:image/${initialValues.displayFileName?.split('.').pop()};base64,${base64}`)
      })
    } else {
      setSrc('')
    }
  }, [initialValues.documentGuid, initialValues.displayFileName, vehicleGuid])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnChange
        validateOnBlur={false}
        validationSchema={vehicleOdometerAfterModificationPhotoValidationSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleSubmit, isSubmitting, setFieldValue, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} alignItems='center'>
                <DialogTitle>Odometer After Modification Photo Details</DialogTitle>

                <Grid item xs={12}>
                  <img
                    alt='Odometer After Modification'
                    src={
                      values.photoFile
                        ? URL.createObjectURL(values.photoFile)
                        : src
                    }
                    style={{ width: '100%' }}
                  />
                </Grid>

                <Grid item xs={12}>
                  {errors.photoFile && (
                    <Typography color='error' variant='body2'>
                      {errors.photoFile}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <input
                    accept='image/png, image/jpeg'
                    hidden
                    id='odometerAfterModificationPhotoFileInput'
                    type='file'
                    onChange={event => {
                      if (event.target.files && event.target.files.length > 0) {
                        setFieldValue('photoFile', event.target.files[0])
                      }
                    }}
                  />
                  <Button
                    color='primary'
                    disabled={isSubmitting || isLoading}
                    endIcon={<AddAPhoto />}
                    fullWidth
                    onClick={() => {
                      document.getElementById('odometerAfterModificationPhotoFileInput')?.click()
                    }}
                    variant='outlined'
                  >
                    BROWSE OR TAKE PHOTO
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    color='error'
                    disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                    endIcon={<HideImage />}
                    fullWidth
                    onClick={() => {
                      if (window.confirm('Are you sure you would like to delete the uploaded Odometer After Modification Photo?')) {
                        onDelete()
                      }
                    }}
                    variant='outlined'
                  >
                    DELETE UPLOADED PHOTO
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <GreenText variant='outlined'>
                    {`**Photo Previewer:**

Displays a preview of the uploaded Odometer After Modification photo.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button allows the user to take a new photo or upload an existing photo.

**DELETE UPLOADED PHOTO Button:**

Clicking this button removes the persisted photo.
`}
                  </GreenText>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color='error' disabled={isSubmitting || isLoading} onClick={onClose} variant='text'>
                CANCEL
              </Button>
              <Button color='primary' disabled={isSubmitting || isLoading} type='submit' variant='contained'>
                SAVE
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  )
}
